/**
 * Bridge between an antd Form and the per-session rule store. Keeps the antd
 * Form as the renderer/validator while the store is the source of truth:
 *
 * - hydrate-on-mount: if the store already holds this scope's values (e.g. a
 *   cross-section reaction wrote them while this form was unmounted), push them
 *   into the form; otherwise seed the store from the form.
 * - register a setter so reactive effects (reset/autoFill) targeting this scope
 *   write straight into the form (antd `setFieldsValue` does not re-fire
 *   `onValuesChange`, so there is no loop).
 * - `onValuesChange`: forward user edits into the store (which runs reactions).
 * - `derivedStates`: the per-field UI state for the renderer.
 */
import { useCallback, useEffect } from 'react';
import type { FormInstance } from 'antd';

import type { Scope } from '../../config/fieldRulesConfig';
import type { ComputeHelpers, FieldUiState, OptionResolvers } from '../types';
import { useRuleStoreApi } from './FieldRulesProvider';
import { useScopeFieldStates } from './selectors';

interface UseRuleBoundFormOptions {
    resolvers?: OptionResolvers;
    helpers?: ComputeHelpers;
    /** For the `promo` scope: isolates this form to its own per-tab working set. */
    instanceId?: string;
}

export interface UseRuleBoundFormResult {
    derivedStates: Record<string, FieldUiState>;
    onValuesChange: (changed: Record<string, unknown>) => void;
    syncToStore: (values: Record<string, unknown>, source?: 'user' | 'hydrate') => void;
    runCompute: (field: string) => void;
}

export function useRuleBoundForm(
    scope: Scope,
    form: FormInstance,
    options?: UseRuleBoundFormOptions,
): UseRuleBoundFormResult {
    const store = useRuleStoreApi();
    const resolvers = options?.resolvers;
    const helpers = options?.helpers;
    const instanceId = options?.instanceId;
    const isPromoInstance = scope === 'promo' && !!instanceId;

    useEffect(() => {
        store.getState().setRuntime({ resolvers, helpers });
    }, [store, resolvers, helpers]);

    useEffect(() => {
        const api = store.getState();
        if (isPromoInstance) {
            const existing = api.getPromoValues(instanceId!);
            if (Object.keys(existing).length > 0) {
                form.setFieldsValue(existing);
            } else {
                api.setPromoValues(instanceId!, form.getFieldsValue(true), { source: 'hydrate' });
            }
            const unregister = api.registerPromoSetter(instanceId!, (vals) => form.setFieldsValue(vals));
            // Tab closed (or event session left) -> drop this instance's working set.
            return () => {
                unregister();
                store.getState().resetPromoInstance(instanceId!);
            };
        }
        const existing = api.getScopeValues(scope);
        if (Object.keys(existing).length > 0) {
            form.setFieldsValue(existing);
        } else {
            api.setValues(scope, form.getFieldsValue(true), { source: 'hydrate' });
        }
        return api.registerSetter(scope, (vals) => form.setFieldsValue(vals));
    }, [store, scope, form, isPromoInstance, instanceId]);

    const derivedStates = useScopeFieldStates(scope, resolvers, instanceId);

    const onValuesChange = useCallback(
        (changed: Record<string, unknown>) => {
            if (isPromoInstance) store.getState().setPromoValues(instanceId!, changed, { source: 'user' });
            else store.getState().setValues(scope, changed, { source: 'user' });
        },
        [store, scope, isPromoInstance, instanceId],
    );

    const syncToStore = useCallback(
        (values: Record<string, unknown>, source: 'user' | 'hydrate' = 'hydrate') => {
            if (isPromoInstance) store.getState().setPromoValues(instanceId!, values, { source });
            else store.getState().setValues(scope, values, { source });
        },
        [store, scope, isPromoInstance, instanceId],
    );

    const runCompute = useCallback(
        (field: string) => {
            store.getState().runCompute(`${scope}:${field}`);
        },
        [store, scope],
    );

    return { derivedStates, onValuesChange, syncToStore, runCompute };
}
